import React, { useState, useEffect } from 'react';
import { Mail, Phone, MapPin, MessageCircle, Send, CheckCircle, AlertCircle, User, Building, X } from 'lucide-react';
import emailjs from '@emailjs/browser';
import "../styles/ContactModal.css";

export function ModernContactForm() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    address: '',
    city: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success', 'error', null

  // Configuración de EmailJS
  const EMAILJS_CONFIG = {
    serviceId: 'service_l26uxzg', // Reemplaza con tu Service ID
    templateId: 'template_o3emzdj', // Reemplaza con tu Template ID
    publicKey: 'user_RSdvYtoUhj5xUGajqzObA' // Reemplaza con tu Public Key
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeModal = () => {
    const modalElement = document.getElementById('contactModal');

    if (modalElement) {
      modalElement.classList.add('fade');
      modalElement.classList.remove('show');

      setTimeout(() => {
        modalElement.style.display = 'none';
        modalElement.setAttribute('aria-hidden', 'true');
        modalElement.removeAttribute('aria-modal');
        modalElement.removeAttribute('role');

        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => {
          backdrop.classList.remove('show');
          setTimeout(() => backdrop.remove(), 150);
        });

        document.body.classList.remove('modal-open');
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        document.body.removeAttribute('style');

        document.removeEventListener('keydown', handleEscapeKey);
      }, 150);
    }
  };

  const handleEscapeKey = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      // Preparar los datos para EmailJS con los nombres exactos de la plantilla
      const templateParams = {
        to_name: 'Mayolo', // Nombre del destinatario
        from_name: formData.name,
        from_email: formData.email,
        name: formData.name,
        email: formData.email,
        address: formData.address,
        city: formData.city,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message,
        // Campos adicionales para la plantilla
        reply_to: formData.email
      };

      // Enviar email usando EmailJS
      const response = await emailjs.send(
        EMAILJS_CONFIG.serviceId,
        EMAILJS_CONFIG.templateId,
        templateParams,
        EMAILJS_CONFIG.publicKey
      );

      console.log('Email enviado exitosamente:', response);
      setSubmitStatus('success');

      // Limpiar el formulario después del envío exitoso
      setFormData({
        name: '',
        email: '',
        address: '',
        city: '',
        phone: '',
        subject: '',
        message: ''
      });

      // Cerrar el modal después de 2 segundos
      setTimeout(() => {
        closeModal();
      }, 2000);

    } catch (error) {
      console.error('Error al enviar el email:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: '',
      email: '',
      address: '',
      city: '',
      phone: '',
      subject: '',
      message: ''
    });
    setSubmitStatus(null);
    closeModal();
  };

  useEffect(() => {
    const modalElement = document.getElementById('contactModal');
    if (modalElement) {
      const handleModalShow = () => {
        document.body.style.overflow = 'hidden';
        document.body.classList.add('modal-open');
        document.addEventListener('keydown', handleEscapeKey);

        if (!document.querySelector('.modal-backdrop')) {
          const backdrop = document.createElement('div');
          backdrop.className = 'modal-backdrop fade show';
          backdrop.onclick = handleCancel;
          document.body.appendChild(backdrop);
        }
      };

      const handleModalHidden = () => {
        document.body.style.overflow = '';
        document.body.classList.remove('modal-open');
        document.body.removeAttribute('style');
        document.removeEventListener('keydown', handleEscapeKey);

        setFormData({
          name: '',
          email: '',
          address: '',
          city: '',
          phone: '',
          subject: '',
          message: ''
        });
        setSubmitStatus(null);
      };

      if (window.bootstrap) {
        modalElement.addEventListener('show.bs.modal', handleModalShow);
        modalElement.addEventListener('hidden.bs.modal', handleModalHidden);

        return () => {
          modalElement.removeEventListener('show.bs.modal', handleModalShow);
          modalElement.removeEventListener('hidden.bs.modal', handleModalHidden);
        };
      } else {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'style') {
              const isVisible = modalElement.style.display !== 'none' && modalElement.classList.contains('show');
              if (isVisible && !modalElement.dataset.modalShown) {
                modalElement.dataset.modalShown = 'true';
                handleModalShow();
              } else if (!isVisible && modalElement.dataset.modalShown) {
                modalElement.dataset.modalShown = 'false';
                handleModalHidden();
              }
            }
          });
        });

        observer.observe(modalElement, { attributes: true });

        return () => {
          observer.disconnect();
        };
      }
    }
  }, []);

  return (
    <div
      className="modal fade"
      id="contactModal"
      tabIndex="-1"
      aria-labelledby="contactModalLabel"
      aria-hidden="true"
      data-bs-backdrop="true"
      data-bs-keyboard="true"
    >
      <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
        <div className="modal-content">

          {/* Header del modal */}
          <div className="modal-header">
            <div>
              <span className="cform__eyebrow">Free estimate</span>
              <h2 className="modal-title" id="contactModalLabel">
                Request your quote
              </h2>
              <p className="cform__sub">
                Tell us the space and we come back with a price.
              </p>
            </div>
            <button
              type="button"
              className="cform__close"
              onClick={handleCancel}
              aria-label="Close"
              disabled={isSubmitting}
            >
              <X size={18} />
            </button>
          </div>

          {/* Body del modal */}
          <div className="modal-body">
            {/* Mensaje de estado */}
            {submitStatus && (
              <div
                className={`cform__status ${
                  submitStatus === 'success' ? 'cform__status--ok' : 'cform__status--err'
                }`}
                role="status"
              >
                {submitStatus === 'success' ? (
                  <>
                    <CheckCircle size={20} aria-hidden="true" />
                    Request sent. We'll contact you soon.
                  </>
                ) : (
                  <>
                    <AlertCircle size={20} aria-hidden="true" />
                    The request didn't go through. Check your connection and send it again.
                  </>
                )}
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="cform__grid">
                {/* Nombre y Email */}
                <div className="cform__field">
                  <label className="cform__label" htmlFor="cf-name">
                    <User size={14} aria-hidden="true" />
                    Name
                  </label>
                  <input
                    id="cf-name"
                    type="text"
                    name="name"
                    className="cform__input"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Write your name"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="cform__field">
                  <label className="cform__label" htmlFor="cf-email">
                    <Mail size={14} aria-hidden="true" />
                    E-mail
                  </label>
                  <input
                    id="cf-email"
                    type="email"
                    name="email"
                    className="cform__input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="you@email.com"
                    required
                    disabled={isSubmitting}
                  />
                </div>

                {/* Dirección */}
                <div className="cform__field cform__field--wide">
                  <label className="cform__label" htmlFor="cf-address">
                    <MapPin size={14} aria-hidden="true" />
                    Address
                  </label>
                  <input
                    id="cf-address"
                    type="text"
                    name="address"
                    className="cform__input"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Full service address"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Ciudad y Teléfono */}
                <div className="cform__field">
                  <label className="cform__label" htmlFor="cf-city">
                    <Building size={14} aria-hidden="true" />
                    City
                  </label>
                  <input
                    id="cf-city"
                    type="text"
                    name="city"
                    className="cform__input"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Your city"
                    disabled={isSubmitting}
                  />
                </div>

                <div className="cform__field">
                  <label className="cform__label" htmlFor="cf-phone">
                    <Phone size={14} aria-hidden="true" />
                    Phone
                  </label>
                  <input
                    id="cf-phone"
                    type="tel"
                    name="phone"
                    className="cform__input mono"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="(555) 123-4567"
                    disabled={isSubmitting}
                  />
                </div>

                {/* Asunto */}
                <div className="cform__field cform__field--wide">
                  <label className="cform__label" htmlFor="cf-subject">
                    <MessageCircle size={14} aria-hidden="true" />
                    Type of service
                  </label>
                  <select
                    id="cf-subject"
                    name="subject"
                    className="cform__input"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Select type of service</option>
                    <option value="Window Cleaning">Window Cleaning</option>
                    <option value="Steam Carpet and Furniture Cleaning">Steam Carpet and Furniture Cleaning</option>
                    <option value="Disinfecting Office Cleaning">Disinfecting Office Cleaning</option>
                    <option value="Pressure Washing">Pressure Washing</option>
                    <option value="House Cleaning">House Cleaning</option>
                    <option value="New Construction Cleaning">New Construction Cleaning</option>
                    <option value="Other Service">Other</option>
                  </select>
                </div>

                {/* Mensaje */}
                <div className="cform__field cform__field--wide">
                  <label className="cform__label" htmlFor="cf-message">
                    <MessageCircle size={14} aria-hidden="true" />
                    Service details
                  </label>
                  <textarea
                    id="cf-message"
                    name="message"
                    className="cform__input"
                    rows="4"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder="Describe what type of cleaning you need, size of the space, desired frequency, and any special details..."
                    disabled={isSubmitting}
                  ></textarea>
                </div>
              </div>

              {/* Botones */}
              <div className="cform__actions">
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-sun"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="cform__spinner" aria-hidden="true"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={16} aria-hidden="true" />
                      Send request
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
